export type PortfolioMetric = {
  label: string;
  value: string;
  detail: string;
};

export type PortfolioService = {
  title: string;
  description: string;
};

export type PortfolioExperience = {
  company: string;
  role: string;
  period: string;
  impact: string;
};

export type PortfolioProject = {
  name: string;
  summary: string;
  stack: string[];
  url: string;
  repo: string;
  highlight: string;
};

export type PortfolioAIProject = {
  id: string;
  name: string;
  summary: string;
  stack: string[];
  highlight: string;
  status: string;
  demoUrl: string;
  repoUrl: string;
};

export type PortfolioTestimonial = {
  name: string;
  role: string;
  quote: string;
};

export type PortfolioContent = {
  profile: {
    name: string;
    role: string;
    location: string;
    availability: string;
    summary: string;
    social: Array<{ label: string; href: string }>;
    metrics: PortfolioMetric[];
  };
  skills: string[];
  services: PortfolioService[];
  experience: PortfolioExperience[];
  projects: PortfolioProject[];
  aiProjects: PortfolioAIProject[];
  testimonials: PortfolioTestimonial[];
};

export const portfolioContent: PortfolioContent = {
  profile: {
    name: 'Subhash Chandra Bose Thangaraj',
    role: 'Full-Stack Developer and Technical Lead',
    location: 'Dubai, UAE',
    availability:
      'Open to AI Engineer and Full-Stack Developer opportunities in UAE and remote teams.',
    summary:
      'Full-Stack Developer and Technical Lead with 12+ years of experience building high-performance web and mobile applications across telecom, banking, e-commerce, healthcare, and insurance. Strong in React, Next.js, React Native, Flutter, Node.js, AI integration, and Agile team leadership.',
    social: [
      { label: 'LinkedIn', href: 'https://www.linkedin.com/in/tksubhashraj/' },
      { label: 'Email', href: 'mailto:tksubhashraj14@gmail.com' },
      { label: 'Phone', href: 'tel:+971554605038' },
    ],
    metrics: [
      { label: 'Experience', value: '12+ Years', detail: 'Full-stack engineering and technical leadership' },
      { label: 'Teams Led', value: '8 Engineers', detail: 'Mentored delivery teams with TypeScript and CI/CD standards' },
      { label: 'Scale Delivered', value: '1M+ Users', detail: 'Enterprise banking products and telecom apps' },
    ],
  },
  skills: [
    'React.js',
    'Next.js',
    'React Native',
    'Flutter',
    'TypeScript',
    'JavaScript',
    'Node.js',
    'Express.js',
    'PostgreSQL',
    'MongoDB',
    'GraphQL',
    'Supabase',
    'Swift (Intermediate)',
    'Kotlin (Intermediate)',
    'Docker',
    'Kubernetes',
    'Jenkins',
    'AWS',
    'Azure',
    'OpenAI API',
    'LangChain',
    'Storybook',
    'Tailwind CSS',
    'Material UI',
  ],
  services: [
    {
      title: 'Web and Mobile Product Architecture',
      description:
        'Architect and deliver production systems across React, Next.js, React Native, and Flutter with reusable design systems.',
    },
    {
      title: 'Backend and Microservices',
      description:
        'Build secure APIs and microservices with Node.js, Express.js, and scalable data layers for enterprise workflows.',
    },
    {
      title: 'AI and LLM Integration',
      description:
        'Integrate OpenAI and Gen AI workflows for assistants, dynamic user journeys, and decision support experiences.',
    },
  ],
  experience: [
    {
      company: 'FZ SHAMS - EDGE Technologies LLC (TAMM - WIO Bank Project), Abu Dhabi, UAE',
      role: 'Sr. Software Engineer (Tech Lead)',
      period: 'Aug 2025 - Nov 2025',
      impact:
        'Integrated TAMM with government services, built WIO Bank SME onboarding journeys, and implemented AI conversational chatbot flows with secure microservices.',
    },
    {
      company: 'Linnk Technology Services LLC (Client: Du Telecom), Dubai, UAE',
      role: 'Sr. Software Engineer (Tech Lead)',
      period: 'Jun 2023 - Jun 2025',
      impact:
        'Led cross-platform app delivery (React Native, Next.js), reduced service activation time by 50%, and cut production issues by 30% through CI/CD and standards.',
    },
    {
      company: 'NIIT Technologies FZ LLC / Coforge (Client: Mashreq Bank), Dubai, UAE',
      role: 'Senior Dev Engineer',
      period: 'Sep 2020 - May 2023',
      impact:
        'Delivered enterprise React Native and React apps for 1M+ users and built reusable UI component packages with Storybook for scalable frontend delivery.',
    },
    {
      company: 'AppsIntegra Technologies LLC, Dubai, UAE',
      role: 'Senior Dev Engineer',
      period: 'Sep 2019 - Aug 2020',
      impact:
        'Built Arabee learning apps (200K+ downloads, 4.6 rating) and developed NRTC Fresh grocery apps that increased average order value by 18%.',
    },
    {
      company: 'Sword Global India Pvt Ltd, Chennai, India',
      role: 'Analyst',
      period: 'Aug 2017 - Sep 2019',
      impact:
        'Developed education and insurance portals using React.js and Node.js and reduced turnaround time by 30%.',
    },
    {
      company: 'Earlier Experience, Chennai, India',
      role: 'Software Engineer',
      period: 'Jun 2013 - Aug 2017',
      impact:
        'Worked at SRAM & MRAM Resources Technologies, K2B Solutions, and TechZarInfo; delivered enterprise web platforms across education, e-commerce, and insurance domains using React.js, Node.js, and PHP (WordPress, CodeIgniter).',
    },
  ],
  projects: [
    {
      name: 'Du App',
      summary:
        'Telecom app ecosystem built with cross-platform mobile and web flows for service activation and customer journeys.',
      stack: ['React Native', 'Next.js', 'Node.js', 'GraphQL', 'Redux-Saga', 'Swift', 'Kotlin'],
      url: '#',
      repo: '#',
      highlight: '50% faster service activation',
    },
    {
      name: 'Mashreq and Neo App',
      summary:
        'Enterprise banking app experience for large-scale user journeys with integrated backend services.',
      stack: ['React Native', 'React.js', 'Java Microservices'],
      url: '#',
      repo: '#',
      highlight: 'Delivered at 1M+ user scale',
    },
    {
      name: 'Arabee Learning (Student and Family)',
      summary:
        'Education platform mobile apps with stable performance and engaging UX for students and families.',
      stack: ['Flutter', 'Dart', 'Firebase'],
      url: 'https://www.arabeelearning.com/',
      repo: '#',
      highlight: '200K+ downloads and 4.6 rating',
    },
    {
      name: 'NRTC Fresh',
      summary:
        'E-commerce grocery app with checkout and maps integration to improve ordering outcomes.',
      stack: ['React Native', 'Stripe', 'Google Maps', 'React.js'],
      url: 'https://www.nrtcfresh.com/',
      repo: '#',
      highlight: '18% increase in average order value',
    },
  ],
  aiProjects: [
    {
      id: 'adcraft-ai',
      name: 'AdCraft AI',
      summary:
        'AI-assisted ad copy generator for growth teams. It creates campaign-ready headlines, hooks, and CTAs from simple product inputs.',
      stack: ['Next.js', 'TypeScript', 'OpenAI API', 'Prompt Engineering'],
      highlight: 'Fast ad copy iteration',
      status: 'Live Concept',
      demoUrl: '/generate-blog',
      repoUrl: '#',
    },
    {
      id: 'chat-bots',
      name: 'Chat Bots',
      summary:
        'Domain-specific chat assistants for customer support and onboarding with context memory, guarded responses, and fallback routing.',
      stack: ['Node.js', 'LangChain', 'Vector Search', 'REST APIs'],
      highlight: 'Conversational automation',
      status: 'Production Pattern',
      demoUrl: '#ai-projects',
      repoUrl: '#',
    },
  ],
  testimonials: [
    {
      name: 'TAMM - WIO Bank',
      role: 'GovTech and Banking Integration, 2025',
      quote:
        'Delivered secure onboarding and AI-powered conversational assistance across regulated workflows and integrations.',
    },
    {
      name: 'Du Telecom Program',
      role: 'Technical Leadership, 2023 - 2025',
      quote:
        'Led 8 engineers, standardized TypeScript and CI/CD, and improved quality with measurable production incident reduction.',
    },
  ],
};
